import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Film, Users, BarChart3, Settings, Plus, Trash2, Edit, Eye,
  TrendingUp, Layers, Upload, X, Save, Search, Play, Tv,
  UserX, UserCheck, KeyRound, ChevronLeft, ChevronRight,
  Tag, Globe, Star, CreditCard, Check, Ticket, Calendar, Percent,
  Volume2, Joystick, ChevronUp, ChevronDown, RefreshCw, Sparkles, SlidersHorizontal, AlertTriangle, Scissors, GripVertical
} from 'lucide-react';
import { TrimEditor, fmtTrim } from './TrimEditor';
import { AiTrailerButton, AiTrailerBulkButton } from './AiTrailer';
import { ConfirmModal } from './TrashTab';
import FilterTimelineEditor from '../FilterTimelineEditor';
import { LanguageStats } from '../analytics/LanguageStats';
import {
  API, CATEGORIES_LIST, ALL_COUNTRIES, MEDIA_SPECS, LANGUAGES, TYPE_LABELS, TYPE_LABELS_PLURAL, uploadFileToS3, FilterBadge,
  formatFileSize, TimecodeInput, CountryPicker, FilterSelect, LoadingSpinner as SharedLoadingSpinner, validateMediaFile
} from './shared';
import { EpisodePreview, EpisodePreviewModal } from './EpisodePreview';
import { LoadingSpinner } from './core';

export function AvatarsTab({ theme }) {
  const light = theme === 'light';
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCatName, setNewCatName] = useState('');
  const [newAvatarUrl, setNewAvatarUrl] = useState('');
  const [addingTo, setAddingTo] = useState(null);
  const [uploading, setUploading] = useState(false);

  const inputCls = light ? 'bg-gray-50 border border-gray-200 text-gray-900' : 'bg-white/5 border border-white/10 text-white';
  const cardCls = light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5';
  const hintCls = light ? 'text-gray-400' : 'text-white/30';

  const reload = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/avatar-gallery`, { withCredentials: true });
      setCategories(data);
    } catch {}
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const addCategory = async () => {
    if (!newCatName.trim()) return;
    await axios.post(`${API}/admin/avatar-gallery`, { name: newCatName.trim(), avatars: [] }, { withCredentials: true });
    setNewCatName('');
    await reload();
  };

  const deleteCategory = async (catId) => {
    if (!window.confirm('Supprimer cette catégorie d\'avatars ?')) return;
    await axios.delete(`${API}/admin/avatar-gallery/${catId}`, { withCredentials: true });
    await reload();
  };

  const addAvatar = async (catId) => {
    if (!newAvatarUrl.trim()) return;
    const cat = categories.find(c => c.id === catId);
    if (!cat) return;
    const updated = [...(cat.avatars || []), newAvatarUrl.trim()];
    await axios.put(`${API}/admin/avatar-gallery/${catId}`, { avatars: updated }, { withCredentials: true });
    setNewAvatarUrl('');
    setAddingTo(null);
    await reload();
  };

  const uploadAvatar = async (catId, file) => {
    setUploading(true);
    try {
      // Get presigned URL
      const { data: presign } = await axios.post(`${API}/upload/presign`, {
        filename: file.name, content_type: file.type, folder: 'avatars'
      }, { withCredentials: true });
      // Upload to S3
      await axios.put(presign.upload_url, file, { headers: { 'Content-Type': file.type } });
      // Add URL to category
      const cat = categories.find(c => c.id === catId);
      if (cat) {
        const updated = [...(cat.avatars || []), presign.file_url];
        await axios.put(`${API}/admin/avatar-gallery/${catId}`, { avatars: updated }, { withCredentials: true });
        await reload();
      }
    } catch (e) { console.error(e); }
    setUploading(false);
  };

  const removeAvatar = async (catId, idx) => {
    const cat = categories.find(c => c.id === catId);
    if (!cat) return;
    const updated = cat.avatars.filter((_, i) => i !== idx);
    await axios.put(`${API}/admin/avatar-gallery/${catId}`, { avatars: updated }, { withCredentials: true });
    await reload();
  };

  if (loading) return <LoadingSpinner theme={theme} />;

  return (
    <div className="space-y-6" data-testid="avatars-tab">
      <h1 className={`text-2xl font-bold ${light ? 'text-gray-900' : 'text-white'}`}>Galerie d'Avatars</h1>
      <p className={`text-xs ${hintCls}`}>Gérez les avatars disponibles pour les profils utilisateurs.</p>

      {/* Add category */}
      <div className={`rounded-xl p-5 border ${cardCls}`}>
        <h3 className={`text-sm font-semibold mb-3 ${light ? 'text-gray-800' : 'text-white'}`}>Nouvelle catégorie</h3>
        <div className="flex gap-2">
          <input value={newCatName} onChange={e => setNewCatName(e.target.value)} placeholder="Nom de la catégorie (ex: Classiques, Animaux...)"
            onKeyDown={e => { if (e.key === 'Enter') addCategory(); }}
            className={`rounded-lg px-3 py-2 text-xs flex-1 focus:outline-none focus:border-[#E9641A] ${inputCls}`} />
          <button onClick={addCategory} className="px-4 py-2 bg-[#E9641A] text-white text-xs font-semibold rounded-lg">Ajouter</button>
        </div>
      </div>

      {/* Categories list */}
      {categories.map(cat => (
        <div key={cat.id} className={`rounded-xl border p-5 ${cardCls}`} data-testid={`avatar-cat-${cat.id}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-sm font-bold ${light ? 'text-gray-800' : 'text-white'}`}>{cat.name}</h3>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] ${hintCls}`}>{(cat.avatars || []).length} avatars</span>
              <button onClick={() => deleteCategory(cat.id)} className={`text-xs ${light ? 'text-red-400 hover:text-red-600' : 'text-red-400/50 hover:text-red-400'}`}>Supprimer</button>
            </div>
          </div>

          {/* Avatars grid */}
          <div className="flex gap-2 flex-wrap mb-4">
            {(cat.avatars || []).map((url, idx) => (
              <div key={idx} className="relative group">
                <div className="w-16 h-16 rounded-lg overflow-hidden border border-white/10">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                </div>
                <button onClick={() => removeAvatar(cat.id, idx)}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full text-[10px] opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <X size={10} />
                </button>
              </div>
            ))}

            {/* Add avatar button */}
            <div className="flex gap-2">
              <label className={`w-16 h-16 rounded-lg border-2 border-dashed flex items-center justify-center cursor-pointer transition-colors ${
                light ? 'border-gray-200 hover:border-[#E9641A] text-gray-300 hover:text-[#E9641A]' : 'border-white/10 hover:border-[#E9641A] text-white/20 hover:text-[#E9641A]'
              }`}>
                <input type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files[0]) uploadAvatar(cat.id, e.target.files[0]); }} />
                {uploading ? <div className="w-4 h-4 border-2 border-[#E9641A]/30 border-t-[#E9641A] rounded-full animate-spin" /> : <Plus size={20} />}
              </label>
            </div>
          </div>

          {/* Add via URL */}
          {addingTo === cat.id ? (
            <div className="flex gap-2">
              <input value={newAvatarUrl} onChange={e => setNewAvatarUrl(e.target.value)} placeholder="URL de l'image..."
                className={`rounded-lg px-3 py-2 text-xs flex-1 focus:outline-none focus:border-[#E9641A] ${inputCls}`} />
              <button onClick={() => addAvatar(cat.id)} className="px-3 py-2 bg-[#E9641A] text-white text-xs font-semibold rounded-lg">OK</button>
              <button onClick={() => { setAddingTo(null); setNewAvatarUrl(''); }} className={`px-3 py-2 text-xs ${hintCls}`}>Annuler</button>
            </div>
          ) : (
            <button onClick={() => setAddingTo(cat.id)} className={`text-xs ${light ? 'text-[#E9641A]' : 'text-[#E9641A]'} hover:underline`}>+ Ajouter via URL</button>
          )}
        </div>
      ))}

      {categories.length === 0 && (
        <p className={`text-sm text-center py-8 ${hintCls}`}>Aucune catégorie d'avatars. Créez-en une pour commencer.</p>
      )}
    </div>
  );
}


